// Time — the period words and their calendar math (ADR-082 §4 · §6).
//
// ZONE-FREE by construction: a word + the zone's TODAY + the week start → whole
// days (`first` included, `next` excluded), or N hours. The zone's edge — a day
// to its first instant — is the backend's (`core/time`, on Temporal): the server
// picks rows; the browser uses these days only for captions and pre-fills, so it
// never needs Temporal. Days are `YYYY-MM-DD` strings end to end; the arithmetic
// runs on UTC epoch days, which have no zone and no daylight saving.
//
// The one exception to "no zone" is `dayIn` — the calendar day an instant falls
// on IN a zone, read through `Intl` (formatting an instant, never midnight math).
import { z } from 'zod';
import { CALENDAR_DAY_RE, isCalendarDay } from './calendar-day.js';
import { IanaZoneSchema } from './zone.js';
/** Words cut on whole days, at midnight in the screen's zone. */
export const PERIOD_DAY_WORDS = [
    'today',
    'yesterday',
    'last_7d',
    'last_30d',
    'last_90d',
    'this_week',
    'this_month',
    'this_year',
    'previous_week',
    'previous_month',
    'previous_year',
];
/** Rolling hours — the only zone-free words. */
export const PERIOD_HOUR_WORDS = ['last_24h', 'last_6h', 'last_1h'];
/** ONE vocabulary for every surface, in the picker's order (ADR-082 §4): a surface
 *  may HIDE a word, never ADD one. `all` = no bound — only where the read keeps
 *  its answer bounded. */
export const PERIOD_WORDS = [...PERIOD_DAY_WORDS, ...PERIOD_HOUR_WORDS, 'all'];
export const PeriodWordSchema = z.enum(PERIOD_WORDS);
const DAY_PAIR_RE = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;
// The years a pair may name — its day after the last must still be a
// `YYYY-MM-DD` day (year 9999's last day has none).
const PAIR_FLOOR = '1000-01-01';
const PAIR_CEILING = '9998-12-31';
/** `YYYY-MM-DD..YYYY-MM-DD` → the pair, or null (an impossible day, a pair that
 *  runs backwards, or a year outside 1000–9998). */
export function parseDayPair(v) {
    const m = DAY_PAIR_RE.exec(v);
    if (!m)
        return null;
    const first = m[1];
    const last = m[2];
    if (!isCalendarDay(first) || !isCalendarDay(last) || first > last)
        return null;
    if (first < PAIR_FLOOR || last > PAIR_CEILING)
        return null;
    return { first, last };
}
export function formatDayPair(p) {
    return `${p.first}..${p.last}`;
}
/** A word or a day pair, as the URL and the wire write it — or null. */
export function parsePeriod(v) {
    return PERIOD_WORDS.includes(v) ? v : parseDayPair(v);
}
// ── the wire (ADR-082 §5 · §6) ───────────────────────────────────────────────
// ONE query key per time axis, named after the field it bounds (`created`),
// holding a word or a pair exactly as the URL does — no second grammar between
// the link and the read. The screen's zone rides beside it as `tz`: the server
// cuts the days in THAT zone (never one it guessed), so a period without its
// zone is refused. Every period-carrying query — and its count sibling — takes
// the pair below; the message is the error CODE, worded in the `errors` words.
/** A period as a query param: a word, or `YYYY-MM-DD..YYYY-MM-DD`; anything else is `INVALID_PERIOD`. */
export const PeriodParamSchema = z.string().max(24).refine((v) => parsePeriod(v) !== null, { message: 'INVALID_PERIOD' });
/** The screen's zone on a period-carrying query (`tz`) — optional on the shape, required by `zoneRequiredWith`. */
export const PeriodZoneSchema = IanaZoneSchema.optional();
/** The refinement a period-carrying query ends with: `tz` must come whenever any of `fields` holds a period —
 *  `TIMEZONE_REQUIRED` on `tz` (a zone alone is harmless; a period alone is a day cut nobody chose). */
export function zoneRequiredWith(fields) {
    return (v, ctx) => {
        if (v.tz === undefined && fields.some((f) => v[f] !== undefined))
            ctx.addIssue({ code: 'custom', path: ['tz'], message: 'TIMEZONE_REQUIRED' });
    };
}
export const WeekStartSchema = z.number().int().min(1).max(7);
// ── calendar arithmetic on epoch days (no zone, no clock) ─────────────────────
const DAY_MS = 86_400_000;
function epochDay(day) {
    const m = CALENDAR_DAY_RE.exec(day);
    if (!m || !isCalendarDay(day))
        throw new RangeError(`not a calendar day: ${day}`);
    return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS;
}
function dayOfEpoch(n) {
    return new Date(n * DAY_MS).toISOString().slice(0, 10);
}
/** `day` moved by `n` calendar days. */
export function addDays(day, n) {
    return dayOfEpoch(epochDay(day) + n);
}
/** Calendar days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from, to) {
    return epochDay(to) - epochDay(from);
}
/** ISO weekday of a day: 1 = Monday … 7 = Sunday. */
export function isoWeekday(day) {
    // 1970-01-01 was a Thursday (ISO 4).
    return ((((epochDay(day) + 3) % 7) + 7) % 7 + 1);
}
function weekStartOf(day, weekStart) {
    return addDays(day, -((isoWeekday(day) - weekStart + 7) % 7));
}
// The epoch day of a year and month — set on a UTC date, never through Date.UTC,
// which reads the years 0–99 as 1900–1999.
function epochOf(year, month) {
    const d = new Date(0);
    d.setUTCFullYear(year, month, 1);
    return d.getTime() / DAY_MS;
}
/** The first day of the month `months` away from `day`'s month. */
function monthStart(day, months = 0) {
    return dayOfEpoch(epochOf(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1 + months));
}
/** The first day of the year `years` away from `day`'s year. */
function yearStart(day, years = 0) {
    return dayOfEpoch(epochOf(Number(day.slice(0, 4)) + years, 0));
}
// ── the resolver ─────────────────────────────────────────────────────────────
/**
 * A period → whole days `[first, next)`, or N rolling hours, or no bound.
 * `today` is the screen zone's calendar day (the caller reads it in the zone);
 * calendar words cover their WHOLE period — "This week" runs to the week's end,
 * never to "now" — and "Last N days" is N calendar days ending today, today
 * included.
 */
export function resolvePeriodDays(period, ctx) {
    const { today, weekStart } = ctx;
    if (typeof period === 'object')
        return { kind: 'days', first: period.first, next: addDays(period.last, 1) };
    const days = (first, next) => ({ kind: 'days', first, next });
    switch (period) {
        case 'today':
            return days(today, addDays(today, 1));
        case 'yesterday':
            return days(addDays(today, -1), today);
        case 'last_7d':
            return days(addDays(today, -6), addDays(today, 1));
        case 'last_30d':
            return days(addDays(today, -29), addDays(today, 1));
        case 'last_90d':
            return days(addDays(today, -89), addDays(today, 1));
        case 'this_week': {
            const start = weekStartOf(today, weekStart);
            return days(start, addDays(start, 7));
        }
        case 'previous_week': {
            const start = weekStartOf(today, weekStart);
            return days(addDays(start, -7), start);
        }
        case 'this_month':
            return days(monthStart(today), monthStart(today, 1));
        case 'previous_month':
            return days(monthStart(today, -1), monthStart(today));
        case 'this_year':
            return days(yearStart(today), yearStart(today, 1));
        case 'previous_year':
            return days(yearStart(today, -1), yearStart(today));
        case 'last_24h':
            return { kind: 'hours', hours: 24 };
        case 'last_6h':
            return { kind: 'hours', hours: 6 };
        case 'last_1h':
            return { kind: 'hours', hours: 1 };
        case 'all':
            return { kind: 'all' };
    }
}
// ── the day an instant falls on, in a zone ───────────────────────────────────
const dayFormatters = new Map();
/** The calendar day `instant` falls on in `zone` (an IANA name). Throws a
 *  RangeError on an unknown zone. */
export function dayIn(zone, instant) {
    let f = dayFormatters.get(zone);
    if (!f) {
        f = new Intl.DateTimeFormat('en-CA', {
            timeZone: zone,
            calendar: 'gregory',
            numberingSystem: 'latn',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        dayFormatters.set(zone, f);
    }
    const parts = f.formatToParts(instant);
    const part = (t) => parts.find((p) => p.type === t)?.value ?? '';
    return `${part('year')}-${part('month')}-${part('day')}`;
}
